import { useEffect, useState } from "react";
import { useAsync } from "react-use";
import { SurveyModel } from "survey-core";

import { DateTime } from "@bps/date-time";

import { ErrorResult } from "../api/base";
import { getDocumentDownloadData, getForm } from "../api/form";
import { hashAccessCode } from "../utils/hashAccessCode";
import { setupModel } from "./setupModel";
import { FormApiOutput, FormStatus, FormStatusEnum } from "./useFormApi.types";
import { useFormId } from "./useFormId";

export const useFormApi = (): FormApiOutput => {
  const { formId, qrcode, preview } = useFormId();
  const [pin, setPin] = useState<string | undefined>();
  const [formStatus, setFormStatus] = useState<FormStatus>({
    status: FormStatusEnum.Initialising,
    form: undefined,
    titleFields: undefined
  });

  const { form, isReadOnly, status, testform, variables } = formStatus;

  const setDeveloperPreviewForm = (form: string) => {
    const formParsed = JSON.parse(form);
    setupModel({
      modelJson: form,
      formDisplaySettings: formParsed.formDisplaySettings,
      isReadOnly: formParsed.isReadOnly,
      variables: formParsed.variables,
      documentDownloadCallBackUrl: formParsed.documentDownloadCallBackUrl,
      formId,
      pin,
      setFormStatus,
      qrcode,
      preview
    });
  };

  // reset pin when form ID changes - probably only will happen when testing
  useEffect(() => {
    setPin(undefined);
  }, [formId]);

  useEffect(() => {
    setFormStatus({
      status: formId ? FormStatusEnum.Loading : FormStatusEnum.NotFound,
      description: "Incorrect Address",
      helpText: ""
    });
  }, [formId, qrcode]);

  const {
    loading,
    error: _errorWithWrongType,
    value
  } = useAsync(
    !form &&
      status !== FormStatusEnum.DecodeError &&
      status !== FormStatusEnum.Completed &&
      status !== FormStatusEnum.IsReadOnly
      ? () => getForm(formId, pin)
      : Promise.resolve,
    [formId, pin]
  );

  // Our error interceptor means the error being returned is our ErrorResult
  //  rather than the typical JavaScript error.  This is probably not the best
  //  way to fix this issue.
  const error = _errorWithWrongType as unknown as ErrorResult | undefined;

  const data = value?.data;

  // The 'validators' property of form elements is passed from the backend as
  // a string, so it needs to be parsed.
  const formData = JSON.parse(data?.formData || "{}");
  formData?.pages?.forEach((page: any) => {
    page.elements?.forEach((element: any) => {
      if (element.validators && typeof element.validators === "string") {
        element.validators = JSON.parse(element.validators);
      }
    });
  });

  if (data) data.formData = JSON.stringify(formData);

  useEffect(() => {
    try {
      if (
        !loading &&
        !error &&
        data &&
        status !== FormStatusEnum.Completed &&
        status !== FormStatusEnum.QrCode &&
        status !== FormStatusEnum.IsReadOnly
      ) {
        const expiry = DateTime.fromISO(data.expiryDate);
        const responseData = data.responseData;
        const isReadOnly = !!data.readOnly;
        formId &&
          data &&
          data.formData &&
          setupModel({
            modelJson: data.formData,
            formDisplaySettings: data.formDisplaySettings,
            documentDownloadCallBackUrl: data.documentDownloadCallBackUrl,
            variables: data.variables,
            formConfig: data.formConfiguration,
            isReadOnly,
            responseData,
            expiry,
            formId,
            pin,
            setFormStatus,
            qrcode,
            preview
          });
      } else if (error) {
        if (
          error.status !== FormStatusEnum.AlreadyCompleted &&
          !testform &&
          !isReadOnly
        ) {
          setFormStatus(error);
        }
      }
    } catch {
      setFormStatus({
        status: FormStatusEnum.DecodeError,
        description: "This form could not be decoded."
      });
    }
  }, [
    loading,
    data,
    formId,
    pin,
    error,
    status,
    qrcode,
    preview,
    isReadOnly,
    testform
  ]);

  const getDocumentDownloadDataWithId = async () => {
    return await getDocumentDownloadData(formId, pin);
  };

  // TODO: explain what this is doing
  if (form) {
    form.onUpdateQuestionCssClasses.add((options: SurveyModel) => {
      const classes = options.cssClasses;
      classes.mainRoot = "bp-question-root";
      classes.title = "bp-question-title";
      classes.root += " bp-question-root";
      classes.item += " bp-question-item";
      classes.label += " bp-question-label";
      classes.error += " bp-question-error";
    });
    form.setVariable("_isReadOnly", isReadOnly);
    form.setVariable("_getDocumentDownloadData", () =>
      getDocumentDownloadData(formId, pin)
    );
    if (variables) {
      Object.keys(formStatus.variables).forEach(key => {
        form.setVariable(key, formStatus.variables[key]);
      });
    }
  }

  return {
    ...formStatus,
    loading,
    setDeveloperPreviewForm,
    submitPin: async (rawPin: string) => {
      const hashedPin = await hashAccessCode(rawPin);
      await getForm(formId, hashedPin);
      // if getForm fails, then an error will be thrown and the pin won't be set
      // TODO - updating state causes a duplicate request
      setPin(hashedPin);
    },
    formId,
    getDocumentDownloadData: getDocumentDownloadDataWithId
  };
};
